import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Globe, Mail } from 'lucide-react';
import { applySeo, resolveMarketingPreviewSeo } from '../../utils/seo';
import { ogBrandImageUrl } from '../../constants/ogBrand';
import { useSiteBranding } from '../../context/SiteBrandingContext';
import { normalizeUiLang } from '../../utils/localePreference';
import { digitalCardStyles } from './digitalCardStyles';
import DigitalCardNav from './DigitalCardNav';
import DigitalCardFooter from './DigitalCardFooter';

const DigitalCardContactPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const { homepageSettings, siteSeo } = useSiteBranding();
  const resolvedLang = normalizeUiLang(i18n.resolvedLanguage || i18n.language);
  const demoIntent = String(searchParams.get('intent') || '').toLowerCase() === 'demo';
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: demoIntent ? 'Request a Demo' : '',
    message: demoIntent ? 'I would like to request a TCards Field Team Tracking & Operations CRM demo.' : '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.tcards.org';
    const defaults = {
      title: t('dcl_contact_seo_title', { defaultValue: 'Contact TCards' }),
      description: t('dcl_contact_seo_desc', {
        defaultValue: 'Have a question, need support or want to learn more about TCards? Get in touch with us.',
      }),
      keywords: ['Contact TCards', 'TCards Support'],
    };
    const seo = resolveMarketingPreviewSeo(
      homepageSettings.previews as Record<string, unknown> | undefined,
      'contact',
      origin,
      defaults,
      siteSeo,
    );
    const canonical = `${origin}/contact`;
    const dispose = applySeo({
      title: seo.title,
      description: seo.description,
      keywords: seo.keywords.length ? seo.keywords : defaults.keywords,
      image: seo.image || ogBrandImageUrl(origin, 'site'),
      url: canonical,
      canonicalUrl: canonical,
      siteName: 'TCards',
      twitterHandle: seo.twitterHandle,
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: seo.title,
        url: canonical,
        description: seo.description,
      },
    });
    return dispose;
  }, [t, resolvedLang, homepageSettings.previews, siteSeo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.email || !form.message) {
      setMsg({ type: 'err', text: t('dcl_contact_fill_required', { defaultValue: 'Please fill in name, email and message.' }) });
      return;
    }
    setSubmitting(true);
    setMsg(null);
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cardOwnerId: 'homepage',
          cardOwnerEmail: 'info@tcards.org',
          cardOwnerName: 'TCards Digital Card',
          fullName: form.name,
          email: form.email,
          phoneNumber: form.phone || undefined,
          message: `[${form.subject || 'General inquiry'}]\n${form.message}`,
          source: 'digital_card_contact',
        }),
      });
      if (!res.ok && res.status !== 202) throw new Error('Failed');
      setMsg({ type: 'ok', text: t('dcl_contact_success', { defaultValue: 'Thank you — your message has been sent. We will get back to you soon.' }) });
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      setMsg({ type: 'err', text: t('dcl_contact_error', { defaultValue: 'Something went wrong. Please try again or email info@tcards.org directly.' }) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dcl-root">
      <style>{digitalCardStyles}</style>
      <DigitalCardNav activePath="/contact" />

      <section className="dcl-section dcl-section--mesh">
        <div className="dcl-container">
          <div className="dcl-contact-grid">
            <div>
              <div className="dcl-section-badge">
                <Mail size={14} />
                {t('dcl_nav_contact', { defaultValue: 'Contact Us' })}
              </div>
              <h1 className="dcl-section-title">
                {t('dcl_contact_title', { defaultValue: 'Contact TCards' })}
              </h1>
              <p className="dcl-section-sub">
                {t('dcl_contact_sub', {
                  defaultValue: 'Have a question, need support or want to learn more about TCards? Get in touch with us.',
                })}
              </p>
              <div className="dcl-contact-info">
                <div className="dcl-contact-info-item">
                  <span className="dcl-contact-info-icon dcl-contact-info-icon--mail">
                    <Mail size={16} />
                  </span>
                  <a href="mailto:info@tcards.org" style={{ color: '#2563EB', fontWeight: 600, textDecoration: 'none' }}>
                    info@tcards.org
                  </a>
                </div>
                <div className="dcl-contact-info-item">
                  <span className="dcl-contact-info-icon dcl-contact-info-icon--web">
                    <Globe size={16} />
                  </span>
                  <span>www.tcards.org</span>
                </div>
              </div>
            </div>

            <div className="dcl-contact-card">
              <form className="dcl-form" onSubmit={handleSubmit}>
                <input
                  className="dcl-input"
                  placeholder={t('dcl_contact_name', { defaultValue: 'Name' })}
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                />
                <input
                  className="dcl-input"
                  type="email"
                  placeholder={t('dcl_contact_email', { defaultValue: 'Email' })}
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  required
                />
                <input
                  className="dcl-input"
                  placeholder={t('dcl_contact_phone', { defaultValue: 'Phone / WhatsApp' })}
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                />
                <input
                  className="dcl-input"
                  placeholder={t('dcl_contact_subject', { defaultValue: 'Subject' })}
                  value={form.subject}
                  onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                />
                <textarea
                  className="dcl-textarea"
                  placeholder={t('dcl_contact_message', { defaultValue: 'Message' })}
                  value={form.message}
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  required
                />
                <button type="submit" className="dcl-btn dcl-btn--primary" disabled={submitting}>
                  {submitting ? t('dcl_contact_sending', { defaultValue: 'Sending…' }) : t('dcl_contact_send', { defaultValue: 'Send Message' })}
                </button>
                {msg && (
                  <div className={`dcl-form-msg dcl-form-msg--${msg.type}`}>{msg.text}</div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      <DigitalCardFooter />
    </div>
  );
};

export default DigitalCardContactPage;
